import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { Audience } from '@/domain/types/audience';
import { useAdminLogger } from '@/domain/use-cases/admin-logger';
import { getDeletionBlock } from '@/domain/use-cases/audiences/audience-deletion';
import {
  countMembers,
  countPublicationsUsing,
  deactivateAudience,
  deleteAudience,
  listAudiences,
} from '@/domain/use-cases/audiences/audience-service';
import Button from '@/presentation/components/button';
import Table, { Column } from '@/presentation/components/table';
import { useCallback, useEffect, useState } from 'react';
import { FiEdit2, FiPlus, FiSlash, FiTrash2, FiUsers } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ActionButton from './components/action-button';
import ListSkeleton from './components/list-skeleton';
import { columns } from './env/columns';

const AudiencesPage = () => {
  const navigate = useNavigate();
  const { logUpdate, logDelete } = useAdminLogger();
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Audiencia cuya eliminación se está comprobando o ejecutando.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Miembros por audiencia. Si el conteo de una falla, simplemente no se muestra.
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});

  const loadAudiences = useCallback(async () => {
    setIsLoading(true);
    try {
      const list = await listAudiences();
      const counts = await Promise.all(
        list.map((audience) =>
          countMembers(audience.id)
            .then((count) => [audience.id, count] as const)
            .catch(() => null),
        ),
      );
      setMemberCounts(
        Object.fromEntries(
          counts.filter(
            (entry): entry is readonly [string, number] => entry !== null,
          ),
        ),
      );
      setAudiences(list);
    } catch (error) {
      toast.error('Error al cargar las audiencias', { theme: 'colored' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAudiences();
  }, [loadAudiences]);

  // Desactivar una audiencia referenciada deja esas publicaciones sin destinatarios
  // nuevos. No se bloquea, pero se advierte con el número exacto.
  const handleDeactivate = async (audience: Audience) => {
    const inUse = await countPublicationsUsing(audience.id);

    const message =
      inUse > 0
        ? `La audiencia "${audience.name}" está referenciada por ${inUse} publicación(es). ` +
          `Desactivarla impide asignarle nuevos miembros, pero quienes ya pertenecen ` +
          `seguirán viendo ese contenido. ¿Continuar?`
        : `¿Desactivar la audiencia "${audience.name}"?`;

    if (!window.confirm(message)) return;

    try {
      await deactivateAudience(audience.id);
      await logUpdate(DOCUMENTS_DB.AUDIENCES, audience.id, audience.name, {
        isActive: false,
      });
      toast.success('Audiencia desactivada', { theme: 'colored' });
      await loadAudiences();
    } catch (error) {
      toast.error('Error al desactivar la audiencia', { theme: 'colored' });
    }
  };

  // Solo se elimina una audiencia inactiva que nadie usa: ni miembros ni
  // publicaciones. Si no, borrarla dejaría IDs apuntando a algo inexistente.
  const handleDelete = async (audience: Audience) => {
    setDeletingId(audience.id);
    try {
      const [memberCount, publicationCount] = await Promise.all([
        countMembers(audience.id),
        countPublicationsUsing(audience.id),
      ]);

      const block = getDeletionBlock({
        isActive: audience.isActive,
        memberCount,
        publicationCount,
      });
      if (block) {
        toast.error(block, { theme: 'colored' });
        return;
      }

      const confirmed = window.confirm(
        `¿Eliminar definitivamente la audiencia "${audience.name}"? Esta acción no se puede deshacer.`,
      );
      if (!confirmed) return;

      await deleteAudience(audience.id);
      await logDelete(DOCUMENTS_DB.AUDIENCES, audience.id, audience.name);
      toast.success('Audiencia eliminada', { theme: 'colored' });
      await loadAudiences();
    } catch (error) {
      toast.error('Error al eliminar la audiencia', { theme: 'colored' });
    } finally {
      setDeletingId(null);
    }
  };

  const actionsColumn: Column<Audience> = {
    header: 'Acciones',
    accessor: 'id',
    render: (_value, audience) => (
      <div className="flex flex-wrap items-center justify-center gap-2">
        <ActionButton
          label="Miembros"
          tone="brand"
          badge={memberCounts[audience.id]}
          icon={<FiUsers size={14} aria-hidden="true" />}
          onClick={() =>
            navigate(`/dashboard/audiences/members/${audience.id}`)
          }
        />
        <ActionButton
          label="Editar"
          tone="neutral"
          icon={<FiEdit2 size={14} aria-hidden="true" />}
          onClick={() =>
            navigate(`/dashboard/audiences/add?edit=${audience.id}`)
          }
        />
        {audience.isActive && (
          <ActionButton
            label="Desactivar"
            tone="danger"
            icon={<FiSlash size={14} aria-hidden="true" />}
            onClick={() => handleDeactivate(audience)}
          />
        )}
        {!audience.isActive && (
          <ActionButton
            label="Eliminar"
            tone="danger"
            icon={<FiTrash2 size={14} aria-hidden="true" />}
            disabled={deletingId === audience.id}
            onClick={() => handleDelete(audience)}
          />
        )}
      </div>
    ),
  };

  const goToAdd = () => navigate('/dashboard/audiences/add');

  return (
    <div className="flex flex-col gap-6 p-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Audiencias</h1>
          <p className="mt-1 text-sm text-gray-600">
            Grupos de usuarios a los que se puede dirigir una publicación
          </p>
        </div>
        <Button
          label="Agregar audiencia"
          icon={<FiPlus />}
          onClick={goToAdd}
          className="shadow-sm"
        />
      </div>

      {isLoading ? (
        <ListSkeleton label="Cargando audiencias" />
      ) : audiences.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-primary">
            <FiUsers size={22} aria-hidden="true" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Aún no hay audiencias
          </h2>
          <p className="max-w-sm text-sm text-gray-600">
            Crea una audiencia para dirigir una publicación solo a un grupo
            específico de usuarios.
          </p>
          <Button
            label="Crear la primera audiencia"
            icon={<FiPlus />}
            onClick={goToAdd}
            className="mt-2"
          />
        </div>
      ) : (
        <Table
          columns={[...columns, actionsColumn]}
          data={audiences}
          className="rounded-lg"
        />
      )}
    </div>
  );
};

export default AudiencesPage;
